// targets.js

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { URLAPI } from "../configuration";
import { _TARGET_TYPES } from "./dataTypes";

dayjs.extend(utc);

export const TARGET_OFF = "targetOff";
export const TARGET_DAY = "targetDay";
export const TARGET_MONTH = "targetMonth";
export const TARGET_YEAR = "targetYear";

export const DEFAULT_TARGET = TARGET_OFF;

export const TARGET_FILTER = {
    [TARGET_OFF]: { label: "Off" },
    [TARGET_DAY]: { label: "Daily" },
    [TARGET_MONTH]: { label: "Monthly" },
    [TARGET_YEAR]: { label: "Yearly" },
};

export const TARGET_GRANULARITY_DAY = "DAY";
export const TARGET_GRANULARITY_MONTH = "MONTH";
export const TARGET_GRANULARITY_YEAR = "YEAR";

export const TARGET_MODE_MANUAL = "MANUAL";
export const TARGET_MODE_COMBINED = "COMBINED";
export const TARGET_MODE_PERCENTAGE = "PERCENTAGE";

export const MANUAL_TARGETS_URL = `${URLAPI}/manual_targets`;
export const MANUAL_TARGET_PERIODS_URL = `${URLAPI}/manual_target_periods`;

export const TARGET_MODES = [
    { value: "manual", apiValue: TARGET_MODE_MANUAL, label: "Manual" },
    { value: "combined", apiValue: TARGET_MODE_COMBINED, label: "Combined" },
    {
        value: "percentage",
        apiValue: TARGET_MODE_PERCENTAGE,
        label: "Percentage",
    },
];

export const TARGET_TYPES = _TARGET_TYPES;

export const TARGET_SUBTYPES = [
    { value: "sessionsTotal" },
    { value: "bookingsTotal" },
    { value: "bookingsRooms" },

    { value: "revenueRoom" },
    { value: "revenueRoomAndExtras" },
    { value: "revenueTotal" },

    { value: "perSessionValue" },
    { value: "websiteConversionRate" },
    { value: "cancellationRate" },
    { value: "ADRTotal" },

    { value: "roomNightsTotal" },
    { value: "vouchersCount" },
    { value: "commissionTotal" },
];

export const TARGET_DATE_TYPES = [
    { value: TARGET_GRANULARITY_DAY, label: "Day" },
    { value: TARGET_GRANULARITY_MONTH, label: "Month" },
    { value: TARGET_GRANULARITY_YEAR, label: "Year" },
];

const MODE_OFF = "off";
const MODE_DAY = "day";
const MODE_MONTH = "month";
const MODE_YEAR = "year";

const TARGET_TYPE_BY_DATATYPE = {
    ResDate: "ResDate",
    CheckIn: "CheckIn",
    CheckOut: "CheckOut",
};

const round2 = (value) => Number(Number(value ?? 0).toFixed(2));

const clone = (value) => JSON.parse(JSON.stringify(value ?? {}));

const hasOwn = (obj, key) =>
    Object.prototype.hasOwnProperty.call(obj ?? {}, key);

const asUtcDay = (value) => {
    const date = dayjs.utc(value).startOf("day");
    return date.isValid() ? date : null;
};

const normalizeMode = (value) => {
    const raw = String(value ?? "").trim();

    const map = {
        [TARGET_OFF]: MODE_OFF,
        [TARGET_DAY]: MODE_DAY,
        [TARGET_MONTH]: MODE_MONTH,
        [TARGET_YEAR]: MODE_YEAR,
        off: MODE_OFF,
        day: MODE_DAY,
        month: MODE_MONTH,
        year: MODE_YEAR,
    };

    return map[raw] ?? MODE_OFF;
};

const getTargetMode = (js) =>
    normalizeMode(js?.filters?.TargetMode ?? js?.filters?.Target);

const getTargetType = (dataType) => TARGET_TYPE_BY_DATATYPE[dataType] ?? null;

const getTargetSubType = (metric) =>
    TARGET_SUBTYPES.some(({ value }) => value === metric) ? metric : null;

const isTargetableMetric = (metric) =>
    TARGET_SUBTYPES.some(({ value }) => value === metric);

const normalizeAxisKey = (value, granularity) => {
    const date = asUtcDay(value);
    if (!date) return "";

    if (granularity === TARGET_GRANULARITY_MONTH) {
        return date.startOf("month").format("YYYY-MM-DD");
    }

    if (granularity === TARGET_GRANULARITY_YEAR) {
        return date.startOf("year").format("YYYY-MM-DD");
    }

    return date.format("YYYY-MM-DD");
};

export const isFullMonthRange = (fromDay, toDay) => {
    const from = asUtcDay(fromDay);
    const to = asUtcDay(toDay);

    return (
        !!from &&
        !!to &&
        from.isSame(from.startOf("month"), "day") &&
        to.isSame(to.endOf("month"), "day")
    );
};

export const isFullYearRange = (fromDay, toDay) => {
    const from = asUtcDay(fromDay);
    const to = asUtcDay(toDay);

    return (
        !!from &&
        !!to &&
        from.isSame(from.startOf("year"), "day") &&
        to.isSame(to.endOf("year"), "day")
    );
};

const resolveGranularity = (mode, fromDay, toDay) => {
    if (mode === MODE_OFF) {
        return { value: null, reason: "target_off" };
    }

    if (mode === MODE_YEAR) {
        return {
            value: TARGET_GRANULARITY_YEAR,
            reason: isFullYearRange(fromDay, toDay)
                ? "target_applied"
                : "target_applied_prorated",
        };
    }

    if (mode === MODE_MONTH) {
        return {
            value: TARGET_GRANULARITY_MONTH,
            reason: isFullMonthRange(fromDay, toDay)
                ? "target_applied"
                : "target_applied_prorated",
        };
    }

    return {
        value: TARGET_GRANULARITY_DAY,
        reason: "target_applied",
    };
};

const buildPieces = (fromDay, toDay, granularity) => {
    const start = asUtcDay(fromDay);
    const end = asUtcDay(toDay);

    if (!start || !end || end.isBefore(start, "day")) return [];

    const pieces = [];

    if (granularity === TARGET_GRANULARITY_YEAR) {
        let cursor = start.startOf("year");
        const limit = end.startOf("year");

        while (!cursor.isAfter(limit, "year")) {
            const periodStart = cursor.startOf("year");
            const periodEnd = cursor.endOf("year").startOf("day");
            const selectedFrom = start.isAfter(periodStart, "day")
                ? start
                : periodStart;
            const selectedTo = end.isBefore(periodEnd, "day") ? end : periodEnd;
            const selectedDays = selectedTo.diff(selectedFrom, "day") + 1;
            const totalDays = periodEnd.diff(periodStart, "day") + 1;

            pieces.push({
                key: normalizeAxisKey(periodStart, granularity),
                fromDay: periodStart.format("YYYY-MM-DD"),
                toDay: periodEnd.format("YYYY-MM-DD"),
                selectedFromDay: selectedFrom.format("YYYY-MM-DD"),
                selectedToDay: selectedTo.format("YYYY-MM-DD"),
                selectedDays,
                totalDays,
                weight: selectedDays / totalDays,
            });

            cursor = cursor.add(1, "year");
        }

        return pieces;
    }

    if (granularity === TARGET_GRANULARITY_MONTH) {
        let cursor = start.startOf("month");
        const limit = end.startOf("month");

        while (!cursor.isAfter(limit, "month")) {
            const periodStart = cursor.startOf("month");
            const periodEnd = cursor.endOf("month").startOf("day");
            const selectedFrom = start.isAfter(periodStart, "day")
                ? start
                : periodStart;
            const selectedTo = end.isBefore(periodEnd, "day") ? end : periodEnd;
            const selectedDays = selectedTo.diff(selectedFrom, "day") + 1;
            const totalDays = periodEnd.diff(periodStart, "day") + 1;

            pieces.push({
                key: normalizeAxisKey(periodStart, granularity),
                fromDay: periodStart.format("YYYY-MM-DD"),
                toDay: periodEnd.format("YYYY-MM-DD"),
                selectedFromDay: selectedFrom.format("YYYY-MM-DD"),
                selectedToDay: selectedTo.format("YYYY-MM-DD"),
                selectedDays,
                totalDays,
                weight: selectedDays / totalDays,
            });

            cursor = cursor.add(1, "month");
        }

        return pieces;
    }

    let cursor = start.startOf("day");

    while (!cursor.isAfter(end, "day")) {
        pieces.push({
            key: normalizeAxisKey(cursor, granularity),
            fromDay: cursor.format("YYYY-MM-DD"),
            toDay: cursor.format("YYYY-MM-DD"),
            selectedFromDay: cursor.format("YYYY-MM-DD"),
            selectedToDay: cursor.format("YYYY-MM-DD"),
            selectedDays: 1,
            totalDays: 1,
            weight: 1,
        });

        cursor = cursor.add(1, "day");
    }

    return pieces;
};

const hasTargetsPayload = (source) => {
    if (!source || typeof source !== "object") return false;

    return (
        (Array.isArray(source.manualTargets) &&
            source.manualTargets.length > 0) ||
        (Array.isArray(source.data) && source.data.length > 0)
    );
};

const getTargetsSource = (dataTargets, dataSimple) => {
    const manualRoot = dataTargets?.manual;
    const directRoot = dataTargets;
    const manualSimple = dataSimple?.dataTargets?.manual;
    const directSimple = dataSimple?.dataTargets;

    if (hasTargetsPayload(manualRoot)) return manualRoot;
    if (hasTargetsPayload(directRoot)) return directRoot;
    if (hasTargetsPayload(manualSimple)) return manualSimple;
    if (hasTargetsPayload(directSimple)) return directSimple;

    return {};
};

const getManualTargets = (dataTargets, dataSimple) => {
    const source = getTargetsSource(dataTargets, dataSimple);

    if (Array.isArray(source.manualTargets)) return source.manualTargets;
    if (Array.isArray(source.data)) return source.data;

    return [];
};

const buildTargetIndex = (dataTargets, dataSimple) => {
    const manualTargets = getManualTargets(dataTargets, dataSimple);

    const exactByCombo = {};
    const availableTypes = new Set();
    const availableSubTypes = new Set();
    const availableGranularities = new Set();

    manualTargets.forEach((row) => {
        const targetType = String(
            row.TargetType ?? row.target_type ?? "",
        ).trim();

        const targetSubType = String(
            row.TargetSubType ?? row.target_subtype ?? "",
        ).trim();

        const granularity = String(
            row.DateGranularity ?? row.date_granularity ?? "",
        )
            .trim()
            .toUpperCase();

        const targetDate = row.TargetDate ?? row.target_date;
        const targetValue = Number(row.TargetValue ?? row.target_value);

        if (
            !targetType ||
            !targetSubType ||
            !granularity ||
            !Number.isFinite(targetValue)
        ) {
            return;
        }

        const comboKey = `${targetType}|${targetSubType}|${granularity}`;
        const axisKey = normalizeAxisKey(targetDate, granularity);

        if (!axisKey) return;

        availableTypes.add(targetType);
        availableSubTypes.add(`${targetType}|${targetSubType}`);
        availableGranularities.add(comboKey);

        if (!exactByCombo[comboKey]) {
            exactByCombo[comboKey] = new Map();
        }

        exactByCombo[comboKey].set(axisKey, round2(targetValue));
    });

    return {
        exactByCombo,
        availableTypes,
        availableSubTypes,
        availableGranularities,
    };
};

const buildBaseMeta = ({ metric, entry, js, mode, baseComparisonType }) => {
    const targetType = getTargetType(js?.dataType);
    const targetSubType = getTargetSubType(metric);
    const baseComparisonValue =
        entry?.resultOneYearBeforeBase?.[metric] ??
        entry?.resultOneYearBefore?.[metric] ??
        null;

    return {
        source: "baseComparison",
        baseComparisonType,
        targetType,
        targetSubType,
        requestedGranularityMode: mode,
        appliedGranularity: null,
        piecesCount: 0,
        pieces: [],
        targetValue: null,
        baseComparisonValue,
        reason: "metric_not_targetable",
    };
};

export const normalizePointDate = (value, dateGranularity) => {
    if (!value) return null;

    const base = dayjs.utc(value);

    if (!base.isValid()) return null;

    if (dateGranularity === TARGET_GRANULARITY_MONTH) {
        return base.startOf("month");
    }

    if (dateGranularity === TARGET_GRANULARITY_YEAR) {
        return base.startOf("year");
    }

    return base.startOf("day");
};

export const getPeriodDistance = (fromDate, toDate, dateGranularity) => {
    const from = asUtcDay(fromDate);
    const to = asUtcDay(toDate);

    if (!from || !to) return 0;

    return Math.abs(to.diff(from, getStepUnit(dateGranularity)));
};

export const getInterpolatedManualValue = (
    axisDate,
    sortedManualPoints,
    dateGranularity,
) => {
    if (!sortedManualPoints.length) return 0;

    let leftPoint = null;
    let rightPoint = null;

    for (const point of sortedManualPoints) {
        if (point.parsedDate.isSame(axisDate, "day")) {
            return round2(point.TargetValue);
        }

        if (point.parsedDate.isBefore(axisDate, "day")) {
            leftPoint = point;
            continue;
        }

        if (point.parsedDate.isAfter(axisDate, "day")) {
            rightPoint = point;
            break;
        }
    }

    if (leftPoint && rightPoint) {
        const totalDistance = getPeriodDistance(
            leftPoint.parsedDate,
            rightPoint.parsedDate,
            dateGranularity,
        );

        if (totalDistance <= 0) return round2(leftPoint.TargetValue);

        const currentDistance = getPeriodDistance(
            leftPoint.parsedDate,
            axisDate,
            dateGranularity,
        );

        const ratio = currentDistance / totalDistance;

        return round2(
            Number(leftPoint.TargetValue) +
                (Number(rightPoint.TargetValue) -
                    Number(leftPoint.TargetValue)) *
                    ratio,
        );
    }

    if (leftPoint) return round2(leftPoint.TargetValue);
    if (rightPoint) return round2(rightPoint.TargetValue);

    return 0;
};

export const getActivePeriodForDate = (periods, axisDate, dateGranularity) => {
    let activePeriod = null;

    for (const period of periods) {
        const validFrom = normalizeValidFromDate(
            period.ValidFrom,
            period.DateGranularity || dateGranularity,
        );

        if (!validFrom) continue;

        if (validFrom.isAfter(axisDate, "day")) break;

        activePeriod = period;
    }

    return activePeriod;
};

export const getLastYearValue = (
    date,
    percentage,
    subtype,
    calculatedDate,
    dateGranularity,
) => {
    const normalizedAxisDate = normalizePointDate(date, dateGranularity);
    const entries = Object.values(calculatedDate?.entries ?? {});

    if (!normalizedAxisDate || entries.length === 0) {
        return 0;
    }

    const hasOwnUsableValue = (obj, key) =>
        Object.prototype.hasOwnProperty.call(obj ?? {}, key) &&
        obj?.[key] !== null &&
        obj?.[key] !== undefined;

    const matchedEntry = entries.find((entry) => {
        const currentFromDate = entry?.result?.fromDateGroup;
        if (currentFromDate) {
            const normalizedCurrentDate = normalizePointDate(
                currentFromDate,
                dateGranularity,
            );

            if (normalizedCurrentDate?.isSame(normalizedAxisDate, "day")) {
                return true;
            }
        }

        const lastYearFromDate = entry?.resultOneYearBefore?.fromDateGroup;
        if (lastYearFromDate) {
            // lastYear dates are already aligned to the current axis in process_
            const normalizedLastYearDate = normalizePointDate(
                lastYearFromDate,
                dateGranularity,
            );

            if (normalizedLastYearDate?.isSame(normalizedAxisDate, "day")) {
                return true;
            }
        }

        return false;
    });

    if (!matchedEntry) {
        return 0;
    }

    const lastYearResult = matchedEntry.resultOneYearBefore ?? {};
    const hasSubtypeValue = hasOwnUsableValue(lastYearResult, subtype);
    const hasRevenueTotalValue = hasOwnUsableValue(
        lastYearResult,
        "revenueTotal",
    );

    let baseValue = 0;

    if (hasSubtypeValue) {
        baseValue = Number(lastYearResult[subtype] ?? 0);
    } else if (hasRevenueTotalValue) {
        baseValue = Number(lastYearResult.revenueTotal ?? 0);
    }

    return round2(baseValue * (1 + Number(percentage ?? 0) / 100));
};

export const normalizeValidFromDate = (value, dateGranularity) => {
    const base = asUtcDay(value);
    if (!base) return null;

    if (dateGranularity === TARGET_GRANULARITY_MONTH)
        return base.startOf("month");
    if (dateGranularity === TARGET_GRANULARITY_YEAR)
        return base.startOf("year");
    return base.startOf("day");
};
export const getStepUnit = (dateGranularity) => {
    if (dateGranularity === TARGET_GRANULARITY_MONTH) return "month";
    if (dateGranularity === TARGET_GRANULARITY_YEAR) return "year";
    return "day";
};

const getPeriodRows = (dataTargets, dataSimple) => {
    const source = getTargetsSource(dataTargets, dataSimple);

    if (Array.isArray(source.periodRows)) return source.periodRows;
    if (Array.isArray(source.periods)) return source.periods;

    return [];
};

const buildTargetRuntime = ({ dataTargets, dataSimple, processFn }) => {
    const manualTargets = getManualTargets(dataTargets, dataSimple);
    const periodRows = getPeriodRows(dataTargets, dataSimple);

    const manualPointsByCombo = {};
    const periodsByCombo = {};
    const availableTypes = new Set();
    const availableSubTypes = new Set();
    const availableGranularities = new Set();
    const calculatedLastYearCache = new Map();

    manualTargets.forEach((row) => {
        const targetType = String(
            row.TargetType ?? row.target_type ?? "",
        ).trim();
        const targetSubType = String(
            row.TargetSubType ?? row.target_subtype ?? "",
        ).trim();
        const granularity = String(
            row.DateGranularity ?? row.date_granularity ?? "",
        )
            .trim()
            .toUpperCase();

        const targetValue = Number(row.TargetValue ?? row.target_value);
        const parsedDate = normalizePointDate(
            row.TargetDate ?? row.target_date,
            granularity,
        );

        if (
            !targetType ||
            !targetSubType ||
            !granularity ||
            !parsedDate ||
            !Number.isFinite(targetValue)
        ) {
            return;
        }

        const comboKey = `${targetType}|${targetSubType}|${granularity}`;

        availableTypes.add(targetType);
        availableSubTypes.add(`${targetType}|${targetSubType}`);
        availableGranularities.add(comboKey);

        if (!manualPointsByCombo[comboKey]) {
            manualPointsByCombo[comboKey] = [];
        }

        manualPointsByCombo[comboKey].push({
            ...row,
            parsedDate,
            TargetValue: round2(targetValue),
        });
    });

    Object.keys(manualPointsByCombo).forEach((comboKey) => {
        manualPointsByCombo[comboKey].sort((left, right) =>
            left.parsedDate.isBefore(right.parsedDate, "day") ? -1 : 1,
        );
    });

    periodRows.forEach((row) => {
        const targetType = String(
            row.TargetType ?? row.target_type ?? "",
        ).trim();
        const targetSubType = String(
            row.TargetSubType ?? row.target_subtype ?? "",
        ).trim();
        const granularity = String(
            row.DateGranularity ?? row.date_granularity ?? "",
        )
            .trim()
            .toUpperCase();

        const parsedValidFrom = normalizeValidFromDate(
            row.ValidFrom ?? row.valid_from,
            granularity,
        );

        if (!targetType || !targetSubType || !granularity || !parsedValidFrom) {
            return;
        }

        const comboKey = `${targetType}|${targetSubType}|${granularity}`;

        availableTypes.add(targetType);
        availableSubTypes.add(`${targetType}|${targetSubType}`);
        availableGranularities.add(comboKey);

        if (!periodsByCombo[comboKey]) {
            periodsByCombo[comboKey] = [];
        }

        periodsByCombo[comboKey].push({
            ...row,
            parsedValidFrom,
        });
    });

    Object.keys(periodsByCombo).forEach((comboKey) => {
        periodsByCombo[comboKey].sort((left, right) =>
            left.parsedValidFrom.isBefore(right.parsedValidFrom, "day")
                ? -1
                : 1,
        );
    });

    const getCalculatedLastYearData = ({
        fromDay,
        toDay,
        granularity,
        targetType,
    }) => {
        if (!processFn) return null;

        const cacheKey = `${targetType}|${granularity}|${fromDay}|${toDay}`;
        if (calculatedLastYearCache.has(cacheKey)) {
            return calculatedLastYearCache.get(cacheKey);
        }

        let groupBy = [];
        if (granularity === TARGET_GRANULARITY_YEAR) {
            groupBy = ["date_year"];
        } else if (granularity === TARGET_GRANULARITY_MONTH) {
            groupBy = ["date_month", "date_year"];
        } else {
            groupBy = ["date_day", "date_month", "date_year"];
        }

        const targetJs = {
            dataType: targetType,
            fromDay,
            toDay,
            fromArrivalDay: "2021-01-01",
            toArrivalDay: "2030-12-31",
            fromResDateDay: "2021-01-01",
            toResDateDay: "2030-12-31",
            filters: {
                Comparison: "previousYear",
            },
            groupBy,
        };

        const calculated = processFn(targetJs);
        calculatedLastYearCache.set(cacheKey, calculated);

        return calculated;
    };

    return {
        manualPointsByCombo,
        periodsByCombo,
        availableTypes,
        availableSubTypes,
        availableGranularities,
        getCalculatedLastYearData,
    };
};

const getExactManualPointMap = (sortedManualPoints) => {
    const map = new Map();

    sortedManualPoints.forEach((row) => {
        map.set(row.parsedDate.format("YYYY-MM-DD"), row);
    });

    return map;
};

const hasOwnUsableValue = (obj, key) =>
    Object.prototype.hasOwnProperty.call(obj ?? {}, key) &&
    obj?.[key] !== null &&
    obj?.[key] !== undefined;

const getLastYearValueFromCalculated = ({
    axisDate,
    percentage,
    subtype,
    calculatedData,
    dateGranularity,
}) => {
    const normalizedAxisDate = normalizePointDate(axisDate, dateGranularity);
    const entries = Object.values(calculatedData?.entries ?? {});

    if (!normalizedAxisDate || entries.length === 0) {
        return 0;
    }

    const matchedEntry = entries.find((entry) => {
        const currentFromDate = entry?.result?.fromDateGroup;
        if (currentFromDate) {
            const normalizedCurrentDate = normalizePointDate(
                currentFromDate,
                dateGranularity,
            );

            if (normalizedCurrentDate?.isSame(normalizedAxisDate, "day")) {
                return true;
            }
        }

        const lastYearFromDate = entry?.resultOneYearBefore?.fromDateGroup;
        if (lastYearFromDate) {
            const normalizedLastYearDate = normalizePointDate(
                lastYearFromDate,
                dateGranularity,
            );

            if (normalizedLastYearDate?.isSame(normalizedAxisDate, "day")) {
                return true;
            }
        }

        return false;
    });

    if (!matchedEntry) return 0;

    const lastYearResult = matchedEntry.resultOneYearBefore ?? {};

    let baseValue = 0;

    if (hasOwnUsableValue(lastYearResult, subtype)) {
        baseValue = Number(lastYearResult[subtype] ?? 0);
    } else if (hasOwnUsableValue(lastYearResult, "revenueTotal")) {
        baseValue = Number(lastYearResult.revenueTotal ?? 0);
    }

    return round2(baseValue * (1 + Number(percentage ?? 0) / 100));
};

const compactPeriod = (period) => {
    if (!period) return null;

    return {
        validFrom: period.ValidFrom ?? period.valid_from ?? null,
        mode: String(
            period.TargetMode ?? period.target_mode ?? "",
        ).toUpperCase(),
        percentageN: Number(period.PercentageN ?? period.percentage_n ?? 0),
    };
};

const resolvePieceTargetValue = ({
    piece,
    targetType,
    targetSubType,
    granularity,
    runtime,
}) => {
    const comboKey = `${targetType}|${targetSubType}|${granularity}`;
    const sortedManualPoints = runtime.manualPointsByCombo[comboKey] ?? [];
    const sortedPeriods = runtime.periodsByCombo[comboKey] ?? [];
    const axisDate = asUtcDay(piece.key);

    if (!axisDate) {
        return { value: null, reason: "invalid_piece_date" };
    }

    const activePeriod = getActivePeriodForDate(
        sortedPeriods,
        axisDate,
        granularity,
    );

    if (!activePeriod) {
        return { value: null, reason: "no_active_period" };
    }

    const exactManualPointMap = getExactManualPointMap(sortedManualPoints);
    const exactManualPoint = exactManualPointMap.get(
        axisDate.format("YYYY-MM-DD"),
    );

    const manualValue = getInterpolatedManualValue(
        axisDate,
        sortedManualPoints,
        granularity,
    );

    const calculatedData = runtime.getCalculatedLastYearData({
        fromDay: piece.fromDay,
        toDay: piece.toDay,
        granularity,
        targetType,
    });

    const percentageValue = getLastYearValueFromCalculated({
        axisDate,
        percentage: Number(activePeriod.PercentageN ?? 0),
        subtype: targetSubType,
        calculatedData,
        dateGranularity: granularity,
    });

    const targetMode = String(activePeriod.TargetMode ?? "").toUpperCase();

    if (targetMode === TARGET_MODE_MANUAL) {
        return {
            value: manualValue,
            reason: "target_applied_manual",
            period: compactPeriod(activePeriod),
        };
    }

    if (targetMode === TARGET_MODE_PERCENTAGE) {
        return {
            value: percentageValue,
            reason: "target_applied_percentage",
            period: compactPeriod(activePeriod),
        };
    }

    if (targetMode === TARGET_MODE_COMBINED) {
        if (exactManualPoint) {
            return {
                value: round2(exactManualPoint.TargetValue),
                reason: "target_applied_combined_manual_point",
                period: compactPeriod(activePeriod),
            };
        }

        return {
            value: percentageValue,
            reason: "target_applied_combined_percentage",
            period: compactPeriod(activePeriod),
        };
    }

    return { value: null, reason: "invalid_target_mode" };
};

const resolveMetricTarget = ({
    metric,
    entry,
    js,
    mode,
    baseComparisonType,
    runtime,
}) => {
    const meta = buildBaseMeta({
        metric,
        entry,
        js,
        mode,
        baseComparisonType,
    });

    if (!isTargetableMetric(metric)) {
        return meta;
    }

    if (mode === MODE_OFF) {
        return {
            ...meta,
            reason: "target_off",
        };
    }

    if (!meta.targetType || !runtime.availableTypes.has(meta.targetType)) {
        return {
            ...meta,
            reason: "no_target_for_type",
        };
    }

    if (
        !meta.targetSubType ||
        !runtime.availableSubTypes.has(
            `${meta.targetType}|${meta.targetSubType}`,
        )
    ) {
        return {
            ...meta,
            reason: "no_target_for_subtype",
        };
    }

    const fromDay =
        entry?.result?.fromDateGroup ??
        entry?.resultOneYearBefore?.fromDateGroup ??
        js?.fromDay;

    const toDay =
        entry?.result?.toDateGroup ??
        entry?.resultOneYearBefore?.toDateGroup ??
        js?.toDay;

    const resolvedGranularity = resolveGranularity(mode, fromDay, toDay);

    if (!resolvedGranularity.value) {
        return {
            ...meta,
            reason: resolvedGranularity.reason,
        };
    }

    const comboKey = `${meta.targetType}|${meta.targetSubType}|${resolvedGranularity.value}`;

    if (!runtime.availableGranularities.has(comboKey)) {
        return {
            ...meta,
            appliedGranularity: resolvedGranularity.value,
            reason: "no_target_for_granularity",
        };
    }

    const pieces = buildPieces(fromDay, toDay, resolvedGranularity.value);

    const resolvedPieces = pieces
        .map((piece) => {
            const resolved = resolvePieceTargetValue({
                piece,
                targetType: meta.targetType,
                targetSubType: meta.targetSubType,
                granularity: resolvedGranularity.value,
                runtime,
            });

            if (!Number.isFinite(Number(resolved.value))) {
                return null;
            }

            const weight = Number(piece.weight ?? 1);
            const rawValue = Number(resolved.value ?? 0);
            const value = round2(rawValue * weight);

            return {
                ...piece,
                rawValue: round2(rawValue),
                value,
                weight: round2(weight),
                reason:
                    weight === 1
                        ? resolved.reason
                        : `${resolved.reason}_prorated`,
                period: resolved.period ?? null,
            };
        })
        .filter(Boolean);

    if (!resolvedPieces.length) {
        return {
            ...meta,
            appliedGranularity: resolvedGranularity.value,
            reason: "no_target_value",
        };
    }

    const isProrated = resolvedPieces.some(
        (piece) => Number(piece.weight ?? 1) !== 1,
    );

    const targetValue = round2(
        resolvedPieces.reduce(
            (acc, piece) => acc + Number(piece.value ?? 0),
            0,
        ),
    );

    if (targetValue <= 0) {
        return {
            ...meta,
            appliedGranularity: resolvedGranularity.value,
            reason: "no_target_value",
        };
    }

    return {
        ...meta,
        source: "target",
        appliedGranularity: resolvedGranularity.value,
        piecesCount: resolvedPieces.length,
        pieces: resolvedPieces,
        targetValue,
        reason: isProrated ? "target_applied_prorated" : "target_applied",
    };
};

export const applyTargetsToEntries = ({
    entries,
    js,
    dataSimple,
    dataTargets,
    baseComparisonType,
    processFn,
}) => {
    const mode = getTargetMode(js);
    const runtime = buildTargetRuntime({
        dataTargets,
        dataSimple,
        processFn,
    });

    const out = {};

    Object.entries(entries ?? {}).forEach(([key, entry]) => {
        const result = clone(entry?.result);
        const resultOneYearBeforeBase = clone(entry?.resultOneYearBefore);
        const resultOneYearBefore = clone(entry?.resultOneYearBefore);
        const comparisonMetaByMetric = {};

        const metricKeys = new Set([
            ...Object.keys(result ?? {}),
            ...Object.keys(resultOneYearBeforeBase ?? {}),
        ]);

        metricKeys.forEach((metric) => {
            const meta = resolveMetricTarget({
                metric,
                entry: {
                    ...entry,
                    result,
                    resultOneYearBefore,
                    resultOneYearBeforeBase,
                },
                js,
                mode,
                baseComparisonType,
                runtime,
            });

            if (meta.source === "target") {
                resultOneYearBefore[metric] = meta.targetValue;
            }

            comparisonMetaByMetric[metric] = meta;
        });

        out[key] = {
            ...entry,
            result,
            resultOneYearBefore,
            resultOneYearBeforeBase,
            comparisonMetaByMetric,
        };
    });

    return out;
};

const TARGET_REASON_LABELS = {
    target_off: "Target overlay is off",
    metric_not_targetable: "This metric does not support targets",
    no_target_for_type: "No target exists for this target type",
    no_target_for_subtype: "No target exists for this metric subtype",
    no_target_for_granularity: "No target exists for this granularity",
    range_not_compatible_with_selected_granularity:
        "Selected range is not compatible with the requested granularity",
    no_target_value: "No target value could be resolved for this range",
    target_applied: "Target applied",
    target_applied_prorated: "Target applied prorated by selected days",
};

const TARGET_SOURCE_LABELS = {
    target: "Target",
    baseComparison: "Base comparison",
};

const TARGET_REQUESTED_MODE_LABELS = {
    off: "Off",
    day: "Daily",
    month: "Monthly",
    year: "Yearly",
};

const getOptionLabel = (options, value) =>
    options.find((item) => item.value === value)?.label ?? value ?? null;

export const getComparisonMeta = (group, metric) =>
    group?.comparisonMetaByMetric?.[metric] ?? null;

export const isTargetComparisonMeta = (meta) => meta?.source === "target";

export const shouldShowTargetComparison = (meta) =>
    !!meta && (meta.requestedGranularityMode ?? MODE_OFF) !== MODE_OFF;

const formatUiDate = (value) => {
    const date = asUtcDay(value);
    return date ? date.format("YYYY-MM-DD") : (value ?? null);
};

const TARGET_PERIOD_MODE_LABELS = {
    [TARGET_MODE_MANUAL]: "Manual",
    [TARGET_MODE_COMBINED]: "Combined",
    [TARGET_MODE_PERCENTAGE]: "Percentage",
};

const getPeriodsUsedUi = (pieces = []) => {
    const seen = new Set();

    return pieces.reduce((acc, piece) => {
        const period = piece?.period;
        if (!period) return acc;

        const key = [
            period.validFrom ?? "",
            String(period.mode ?? "").toUpperCase(),
            Number(period.percentageN ?? 0),
        ].join("|");

        if (seen.has(key)) return acc;
        seen.add(key);

        acc.push({
            validFrom: period.validFrom ?? null,
            mode:
                TARGET_PERIOD_MODE_LABELS[
                    String(period.mode ?? "").toUpperCase()
                ] ??
                period.mode ??
                null,
            percentageN: Number(period.percentageN ?? 0),
        });

        return acc;
    }, []);
};

export const getComparisonMetaUi = (meta) => {
    if (!meta) return null;

    const periodsUsed = (meta.pieces ?? []).reduce((acc, piece) => {
        const period = piece?.period;
        if (!period) return acc;

        const key = [
            period.validFrom ?? "",
            period.mode ?? "",
            Number(period.percentageN ?? 0),
        ].join("|");

        if (acc.some((item) => item.key === key)) return acc;

        acc.push({
            key,
            validFrom: formatUiDate(period.validFrom),
            mode:
                TARGET_PERIOD_MODE_LABELS[
                    String(period.mode ?? "").toUpperCase()
                ] ??
                period.mode ??
                null,
            percentageN: Number(period.percentageN ?? 0),
        });

        return acc;
    }, []);

    return {
        isTarget: isTargetComparisonMeta(meta),
        source:
            TARGET_SOURCE_LABELS[meta.source] ??
            meta.source ??
            "Base comparison",
        targetType: getOptionLabel(TARGET_TYPES, meta.targetType),
        targetSubType: getOptionLabel(TARGET_SUBTYPES, meta.targetSubType),
        requestedGranularityMode:
            TARGET_REQUESTED_MODE_LABELS[meta.requestedGranularityMode] ??
            meta.requestedGranularityMode ??
            null,
        appliedGranularity: getOptionLabel(
            TARGET_DATE_TYPES,
            meta.appliedGranularity,
        ),
        appliedCombo: [
            getOptionLabel(TARGET_DATE_TYPES, meta.appliedGranularity),
            getOptionLabel(TARGET_TYPES, meta.targetType),
            getOptionLabel(TARGET_SUBTYPES, meta.targetSubType),
        ]
            .filter(Boolean)
            .join(" + "),
        periodsUsed,
        piecesCount: meta.piecesCount ?? 0,
        targetValue: meta.targetValue ?? null,
        baseComparisonValue: meta.baseComparisonValue ?? null,
        reason: TARGET_REASON_LABELS[meta.reason] ?? meta.reason ?? null,
    };
};
